import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Tabs,
  Tab,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Pagination,
} from "react-bootstrap";
import Card from "../../../components/Card";
import {
  getFAQs,
  addFAQ,
  updateFAQ,
  deleteFAQ,
} from "../../../services/service";
import "./stylw.css";
import { toast } from "react-toastify";

const FAQManager = () => {
  const [key, setKey] = useState("customer");
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [modal, setModal] = useState({
    show: false,
    editing: false,
    id: null,
    role: "customer",
    question: "",
    answer: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderPagination = (totalItems) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    return (
      <Pagination className="justify-content-center mt-3 flex-wrap">
        <Pagination.First
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        />

        {/* Show first page if not in window */}
        {currentPage > 3 && (
          <>
            <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
            {currentPage > 4 && <Pagination.Ellipsis disabled />}
          </>
        )}

        {/* Window of pages around current page */}
        {[...Array(totalPages)].map((_, i) => {
          const page = i + 1;
          if (page >= currentPage - 2 && page <= currentPage + 2) {
            return (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Pagination.Item>
            );
          }
          return null;
        })}

        {/* Show last page if not in window */}
        {currentPage < totalPages - 2 && (
          <>
            {currentPage < totalPages - 3 && <Pagination.Ellipsis disabled />}
            <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
              {totalPages}
            </Pagination.Item>
          </>
        )}

        <Pagination.Next
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getFAQs();
      setFaqs(res.data);
    } catch (err) {
      setError("Failed to fetch FAQs: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [key]);

  const handleSave = async () => {
    try {
      setLoading(true);
      if (modal.editing) {
        await updateFAQ(modal.id, {
          role: modal.role,
          question: modal.question,
          answer: modal.answer,
        });
        toast.success("Faq updated successfully!");
      } else {
        await addFAQ({
          role: modal.role,
          question: modal.question,
          answer: modal.answer,
        });
        toast.success("Faq added successfully!");
      }
      await fetchData();
      setModal({ ...modal, show: false });
    } catch (err) {
      toast.error("Failed to save FAQ: " + err.message);
      setError("Failed to save FAQ: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setLoading(true);
      await deleteFAQ(deleteId);
      await fetchData();
      setShowDeleteModal(false);
      toast.success("Faq deleted successfully!");
    } catch (err) {
      setError("Failed to delete FAQ: " + err.message);
      toast.error("Failed to delete FAQ: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderRows = (data) => {
    if (!data.length) {
      return (
        <tr>
          <td colSpan="5" className="text-center py-4 text-muted">
            No FAQs found
          </td>
        </tr>
      );
    }

    return data.map((item, index) => {
      const isExpanded = expandedRows[item._id];

      return (
        <tr key={item._id}>
          <td className="text-nowrap">
            {(currentPage - 1) * itemsPerPage + (index + 1)}
          </td>
          <td className="text-break">{item.question}</td>
          <td className="text-break" style={{ minWidth: "200px" }}>
            {isExpanded ? (
              <>
                {item.answer}{" "}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 m-0 align-baseline"
                  onClick={() => toggleExpand(item._id)}
                >
                  read less
                </Button>
              </>
            ) : (
              <>
                {item.answer.length > 100
                  ? item.answer.substring(0, 100) + "... "
                  : item.answer}{" "}
                {item.answer.length > 100 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 m-0 align-baseline"
                    onClick={() => toggleExpand(item._id)}
                  >
                    read more
                  </Button>
                )}
              </>
            )}
          </td>
          <td>
            <span className="badge bg-info text-uppercase">{item.role}</span>
          </td>
          <td>
            <div className="d-flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="warning"
                onClick={() =>
                  setModal({
                    show: true,
                    editing: true,
                    id: item._id,
                    role: item.role,
                    question: item.question,
                    answer: item.answer,
                  })
                }
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  setDeleteId(item._id);
                  setShowDeleteModal(true);
                }}
              >
                Delete
              </Button>
            </div>
          </td>
        </tr>
      );
    });
  };

  const renderTabContent = (role) => {
    const filtered = faqs.filter(
      (f) =>
        f.role === role &&
        (f.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          f.answer.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const paginated = filtered.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    return (
      <div className="table-responsive p-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2">Loading FAQs...</p>
          </div>
        ) : (
          <>
            <table className="table table-striped table-bordered mb-0">
              <thead className="light">
                <tr>
                  <th className="text-nowrap">Sno.</th>
                  <th>Question</th>
                  <th>Answer</th>
                  <th className="text-nowrap">Role</th>
                  <th className="text-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>{renderRows(paginated)}</tbody>
            </table>
            {renderPagination(filtered.length)}
          </>
        )}
      </div>
    );
  };

  return (
    <Row className="mb-4">
      <Col xs="12">
        <Card className="h-100 w-100">
          <Card.Header>
            <div
              style={{ width: "100%" }}
              className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3"
            >
              {/* Left side: Title */}
              <h4 className="card-title mb-2 mb-md-0">FAQ Manager</h4>

              {/* Right side: Search + Add button */}
              <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-50 custom-half">
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-grow-1"
                  style={{ minWidth: "200px" }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  className="w-100 w-sm-auto"
                  onClick={() =>
                    setModal({
                      show: true,
                      editing: false,
                      id: null,
                      role: "customer",
                      question: "",
                      answer: "",
                    })
                  }
                >
                  <i className="fa fa-plus me-1"></i> Add FAQ
                </Button>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="px-0">
            {error && (
              <Alert
                variant="danger"
                className="mx-3"
                dismissible
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            <Tabs
              id="faq-tabs"
              activeKey={key}
              onSelect={(k) => setKey(k)}
              className="px-3"
              mountOnEnter
              unmountOnExit
            >
              <Tab eventKey="customer" title="Customer FAQs">
                {renderTabContent("customer")}
              </Tab>
              <Tab eventKey="vendor" title="Vendor FAQs">
                {renderTabContent("vendor")}
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </Col>

      {/* Add/Edit FAQ Modal */}
      <Modal
        show={modal.show}
        onHide={() => setModal({ ...modal, show: false })}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modal.editing ? "Edit FAQ" : "Add FAQ"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                <Form.Check
                  inline
                  type="radio"
                  label="Customer"
                  checked={modal.role === "customer"}
                  onChange={() => setModal({ ...modal, role: "customer" })}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Vendor"
                  checked={modal.role === "vendor"}
                  onChange={() => setModal({ ...modal, role: "vendor" })}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter question"
                value={modal.question}
                onChange={(e) =>
                  setModal({ ...modal, question: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Answer</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter answer"
                value={modal.answer}
                onChange={(e) => setModal({ ...modal, answer: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end">
          <Button
            variant="secondary"
            onClick={() => setModal({ ...modal, show: false })}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Are you sure you want to delete this FAQ?</p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default FAQManager;

// import React, { useState, useEffect } from "react";
// import {
//   Row,
//   Col,
//   Tabs,
//   Tab,
//   Button,
//   Modal,
//   Form,
//   Alert,
//   Spinner,
//   Pagination,
// } from "react-bootstrap";
// import Card from "../../../components/Card";
// import {
//   getFAQs,
//   addFAQ,
//   updateFAQ,
//   deleteFAQ,
// } from "../../../services/service";

// const FAQManager = () => {
//   const [key, setKey] = useState("customer");
//   const [faqs, setFaqs] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   // Search & Filter
//   const [searchTerm, setSearchTerm] = useState("");

//   // Modal State
//   const [modal, setModal] = useState({
//     show: false,
//     editing: false,
//     id: null,
//     role: "customer",
//     question: "",
//     answer: "",
//   });

//   // Pagination
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 10;

//   // Expand/Collapse state
//   const [expandedRows, setExpandedRows] = useState({});

//   const toggleExpand = (id) => {
//     setExpandedRows((prev) => ({
//       ...prev,
//       [id]: !prev[id],
//     }));
//   };

//   const renderPagination = (totalItems) => {
//     const totalPages = Math.ceil(totalItems / itemsPerPage);
//     if (totalPages <= 1) return null;

//     return (
//       <Pagination className="justify-content-center mt-3">
//         <Pagination.First
//           onClick={() => setCurrentPage(1)}
//           disabled={currentPage === 1}
//         />
//         <Pagination.Prev
//           onClick={() => setCurrentPage(currentPage - 1)}
//           disabled={currentPage === 1}
//         />
//         {Array.from({ length: totalPages }, (_, i) => (
//           <Pagination.Item
//             key={i + 1}
//             active={i + 1 === currentPage}
//             onClick={() => setCurrentPage(i + 1)}
//           >
//             {i + 1}
//           </Pagination.Item>
//         ))}
//         <Pagination.Next
//           onClick={() => setCurrentPage(currentPage + 1)}
//           disabled={currentPage === totalPages}
//         />
//         <Pagination.Last
//           onClick={() => setCurrentPage(totalPages)}
//           disabled={currentPage === totalPages}
//         />
//       </Pagination>
//     );
//   };

//   // Fetch FAQs
//   const fetchData = async () => {
//     setLoading(true);
//     setError("");
//     try {
//       const res = await getFAQs();
//       setFaqs(res.data);
//     } catch (err) {
//       setError("Failed to fetch FAQs: " + err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   // Reset page when switching tabs
//   useEffect(() => {
//     setCurrentPage(1);
//   }, [key]);

//   // Handle Save (Add or Update)
//   const handleSave = async () => {
//     try {
//       setLoading(true);
//       if (modal.editing) {
//         await updateFAQ(modal.id, {
//           role: modal.role,
//           question: modal.question,
//           answer: modal.answer,
//         });
//       } else {
//         await addFAQ({
//           role: modal.role,
//           question: modal.question,
//           answer: modal.answer,
//         });
//       }
//       await fetchData();
//       setModal({ ...modal, show: false });
//     } catch (err) {
//       setError("Failed to save FAQ: " + err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Handle Delete
//   const handleDelete = async (id) => {
//     if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
//     try {
//       setLoading(true);
//       await deleteFAQ(id);
//       await fetchData();
//     } catch (err) {
//       setError("Failed to delete FAQ: " + err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Render rows
//   const renderRows = (data) => {
//     if (data.length === 0) {
//       return (
//         <tr>
//           <td colSpan="5" className="text-center py-4 text-muted">
//             No FAQs found
//           </td>
//         </tr>
//       );
//     }

//     return data.map((item, index) => {
//       const isExpanded = expandedRows[item._id];

//       return (
//         <tr key={item._id}>
//           <td>{(currentPage - 1) * itemsPerPage + (index + 1)}</td>
//           <td>{item.question}</td>
//           <td style={{ cursor: "pointer", minWidth: "400px", whiteSpace: "normal" }}>
//             {isExpanded ? (
//               <>
//                 {item.answer}{" "}
//                 <Button
//                   variant="link"
//                   size="sm"
//                   className="p-0 m-0 align-baseline"
//                   onClick={() => toggleExpand(item._id)}
//                 >
//                   read less
//                 </Button>
//               </>
//             ) : (
//               <>
//                 {item.answer.length > 100
//                   ? item.answer.substring(0, 100) + "... "
//                   : item.answer}{" "}
//                 {item.answer.length > 100 && (
//                   <Button
//                     variant="link"
//                     size="sm"
//                     className="p-0 m-0 align-baseline"
//                     onClick={() => toggleExpand(item._id)}
//                   >
//                     read more
//                   </Button>
//                 )}
//               </>
//             )}
//           </td>
//           <td>
//             <span className="badge bg-info">{item.role}</span>
//           </td>
//           <td>
//             <div className="d-flex gap-2">
//               <Button
//                 size="sm"
//                 variant="warning"
//                 onClick={() =>
//                   setModal({
//                     show: true,
//                     editing: true,
//                     id: item._id,
//                     role: item.role,
//                     question: item.question,
//                     answer: item.answer,
//                   })
//                 }
//               >
//                 Edit
//               </Button>
//               <Button
//                 size="sm"
//                 variant="danger"
//                 onClick={() => handleDelete(item._id)}
//               >
//                 Delete
//               </Button>
//             </div>
//           </td>
//         </tr>
//       );
//     });
//   };

//   // Tab content renderer
//   const renderTabContent = (role) => {
//     const filtered = faqs.filter(
//       (f) =>
//         f.role === role &&
//         (f.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
//           f.answer.toLowerCase().includes(searchTerm.toLowerCase()))
//     );
//     const paginated = filtered.slice(
//       (currentPage - 1) * itemsPerPage,
//       currentPage * itemsPerPage
//     );

//     return (
//       <div className="table-responsive p-3">
//         {loading ? (
//           <div className="text-center py-5">
//             <Spinner animation="border" />
//             <p className="mt-2">Loading FAQs...</p>
//           </div>
//         ) : (
//           <>
//             <table className="table table-striped mb-0">
//               <thead className="light">
//                 <tr>
//                   <th>Sno.</th>
//                   <th>Question</th>
//                   <th>Answer</th>
//                   <th>Role</th>
//                   <th>Action</th>
//                 </tr>
//               </thead>
//               <tbody>{renderRows(paginated)}</tbody>
//             </table>
//             {renderPagination(filtered.length)}
//           </>
//         )}
//       </div>
//     );
//   };

//   return (
//     <>
//       <Row className="mb-4">
//         <Col sm="12">
//           <Card>
//             <Card.Header>
//               <div className="d-flex justify-content-between">
//                 <h4 className="card-title mb-0">FAQ Manager</h4>
//               </div>
//               <div className="d-flex justify-content-between align-items-center mb-4">
//                 <div>
//                   <Form.Control
//                     variant="primary"
//                     size="sm"
//                     type="text"
//                     placeholder="Search FAQs..."
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     style={{ maxWidth: "250px" }}
//                   />
//                 </div>
//                 &nbsp;
//                 <Button
//                   variant="primary"
//                   size="sm"
//                   onClick={() =>
//                     setModal({
//                       show: true,
//                       editing: false,
//                       id: null,
//                       role: "customer",
//                       question: "",
//                       answer: "",
//                     })
//                   }
//                 >
//                   <i className="fa fa-plus me-1"></i> Add FAQ
//                 </Button>
//               </div>
//             </Card.Header>

//             <Card.Body className="px-0">
//               {error && (
//                 <Alert
//                   variant="danger"
//                   className="mx-3"
//                   dismissible
//                   onClose={() => setError("")}
//                 >
//                   {error}
//                 </Alert>
//               )}

//               <Tabs
//                 id="faq-tabs"
//                 activeKey={key}
//                 onSelect={(k) => setKey(k)}
//                 className="px-3"
//               >
//                 <Tab eventKey="customer" title="Customer FAQs">
//                   {renderTabContent("customer")}
//                 </Tab>
//                 <Tab eventKey="vendor" title="Vendor FAQs">
//                   {renderTabContent("vendor")}
//                 </Tab>
//               </Tabs>
//             </Card.Body>
//           </Card>
//         </Col>
//       </Row>

//       {/* Add/Edit FAQ Modal */}
//       <Modal
//         show={modal.show}
//         onHide={() => setModal({ ...modal, show: false })}
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>{modal.editing ? "Edit FAQ" : "Add FAQ"}</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Role</Form.Label>
//               <div>
//                 <Form.Check
//                   inline
//                   type="radio"
//                   label="Customer"
//                   checked={modal.role === "customer"}
//                   onChange={() => setModal({ ...modal, role: "customer" })}
//                 />
//                 <Form.Check
//                   inline
//                   type="radio"
//                   label="Vendor"
//                   checked={modal.role === "vendor"}
//                   onChange={() => setModal({ ...modal, role: "vendor" })}
//                 />
//               </div>
//             </Form.Group>

//             <Form.Group className="mb-3">
//               <Form.Label>Question</Form.Label>
//               <Form.Control
//                 type="text"
//                 placeholder="Enter question"
//                 value={modal.question}
//                 onChange={(e) =>
//                   setModal({ ...modal, question: e.target.value })
//                 }
//               />
//             </Form.Group>

//             <Form.Group className="mb-3">
//               <Form.Label>Answer</Form.Label>
//               <Form.Control
//                 as="textarea"
//                 rows={3}
//                 placeholder="Enter answer"
//                 value={modal.answer}
//                 onChange={(e) =>
//                   setModal({ ...modal, answer: e.target.value })
//                 }
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setModal({ ...modal, show: false })}
//           >
//             Cancel
//           </Button>
//           <Button variant="primary" onClick={handleSave} disabled={loading}>
//             {loading ? <Spinner size="sm" /> : "Save"}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// };

// export default FAQManager;
